'use client'

import { Badge, Box, BoxProps, Text } from '@chakra-ui/react'
import { ItemApi } from 'appTypes/items'
import { JSX, useMemo } from 'react'
import { removeDecimals } from 'utils/removeDecimals'
import { ParsedPricingInfo } from 'utils/apiUtils/prices'
import { Location } from '@leid/api-locations'
import AboutLeidSmartCard from './AboutLeidSmartCard'
import { RentalTimeStats } from '../RentalTimeStats'
import { useTranslation } from 'react-i18next'
import { CustomerInfo } from 'app/utils/authentication'
import { isLeidProduct } from 'app/utils/helpers/helpers'
import { CartFormValues } from 'appTypes/orders'
import { isImmediateRental } from 'app/utils/dates'
import { buildDate } from 'utils/dates'

type Props = BoxProps & {
  product: ItemApi
  location?: Location
  showExtraInfo?: boolean
  card?: boolean
  customerInfo?: CustomerInfo
  values: CartFormValues
}

const PriceInfo = ({
  product,
  showExtraInfo,
  location,
  card = false,
  customerInfo,
  values,
  ...boxProps
}: Props): JSX.Element | null => {
  const { t } = useTranslation('common')

  const getPricingInfoForLocation = (location: Location | undefined, pricingInfo: ParsedPricingInfo[] | undefined) => {
    if (!pricingInfo) {
      return undefined
    }

    if (!location) {
      return pricingInfo[0]
    }

    if (location.priceGroupId) {
      const info = pricingInfo.find((s) => s.priceGroupId === location.priceGroupId)
      if (info) {
        return info
      }
    }

    const defaultInfo = pricingInfo.find((s) => s.priceGroupId === undefined)
    if (defaultInfo) {
      return defaultInfo
    }
    return pricingInfo[0]
  }

  const pricingInfo = useMemo(
    () => getPricingInfoForLocation(location, product.pricingInfo),
    [location, product.pricingInfo],
  )

  const isImmediateOrder = useMemo(
    () => isImmediateRental(buildDate(values.startDate, values.pickupAt)),
    [values.startDate, values.pickupAt],
  )

  if (!product.pricingInfo || product.pricingInfo.length === 0) {
    return null
  }

  return (
    <Box borderRadius="lg" marginBottom={3} {...boxProps}>
      {pricingInfo && pricingInfo.minimalPrice > 0 && (
        <Text fontSize={card ? 'lg' : '2xl'} lineHeight="1.5" fontWeight="semibold">
          {t('from')} {removeDecimals(pricingInfo.minimalPrice)} {t('nok')}
        </Text>
      )}

      {isLeidProduct(product) && (
        <Badge colorPalette="green" size={card ? 'md' : 'lg'} padding={2} marginBottom={2}>
          15% {t('leidSmart.cashback')}
        </Badge>
      )}
      {!customerInfo?.customer && (
        <Badge colorPalette="orange" size="md" padding={2} marginBottom={2}>
          {t('newUser50Discount')}
        </Badge>
      )}

      <RentalTimeStats product={product} />
      {!card && pricingInfo && isLeidProduct(product) && (
        <AboutLeidSmartCard pricing={pricingInfo} isImmediateOrder={isImmediateOrder} />
      )}
    </Box>
  )
}

export default PriceInfo
